import React, { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.jsx';
import { PAGE_ACCESS } from '../../config/menuData.js';
import './login.scss';

// Show the one-click demo accounts in local dev OR when the build is flagged as a
// demo deployment (VITE_DEMO_MODE=1, set for tugos.xamun.ai). Off for real prod.
const showDemo = import.meta.env.DEV || import.meta.env.VITE_DEMO_MODE === '1';
const DEMO_PASSWORD = 'password123';

// Demo accounts grouped by the audience tab they belong to. Clicking a card
// fills the credentials and signs in (dev only).
const DEMO_ACCOUNTS = {
  customer: [
    { name: 'Bayan Shipping Lines', role: 'Customer', email: 'customer@tugops.ph' },
    { name: 'Oceanic Shipping Agencies', role: 'Customer', email: 'oceanic@tugops.ph' },
    { name: 'BlueWave Maritime', role: 'Customer', email: 'bluewave@tugops.ph' },
    { name: 'Sulu Sea Carriers', role: 'Customer', email: 'sulusea@tugops.ph' },
    { name: 'SuperTug Maritime', role: 'Affiliate Operator', email: 'affiliate@tugops.ph' },
    { name: 'Pilot Jose Rizal', role: 'Pilot', email: 'pilot@tugops.ph' },
  ],
  staff: [
    { name: 'Alex Admin', role: 'System Administrator', email: 'admin@example.com' },
    { name: 'Juan de la Cruz', role: 'Operations / Dispatcher', email: 'juan@tugops.ph' },
    { name: 'Maria Clara', role: 'Billing / Finance Officer', email: 'clara.m@tugops.ph' },
    { name: 'Pedro Tarifa', role: 'Pricing / Tariff Manager', email: 'pedro@tugops.ph' },
    { name: 'Cap. Bonifacio', role: 'Tug Captain', email: 'bonifacio@tugops.ph' },
    { name: 'Bea Assisterio', role: 'Sales & Marketing', email: 'assistant@tugops.ph' },
    { name: 'Engr. Reyes', role: 'Maintenance & Bunkering Coordinator', email: 'maint@tugops.ph' },
  ],
};

/**
 * Post-login landing page. `from` may be left over from a previous user's
 * session (e.g. the page they were on at logout), so only honour it when the
 * newly signed-in user's roles can open it; otherwise use /dashboard.
 */
function safeTarget(fromPath, roles = []) {
  if (!fromPath || /^\/(40[13]|500|login|register)$/.test(fromPath)) return '/dashboard';
  const slug = fromPath.replace(/^\/+/, '').split('/')[0];
  const allowed = PAGE_ACCESS[slug];
  if (allowed === 'any') return fromPath;
  if (Array.isArray(allowed) && roles.some((r) => allowed.includes(r))) return fromPath;
  return '/dashboard';
}

const FEATURES = [
  {
    icon: 'fa-bolt',
    title: 'Dispatch at harbor speed',
    desc: 'Plan jobs, assign tugs and captains, and dispatch tickets with live SLA tracking.',
  },
  {
    icon: 'fa-map-marked-alt',
    title: 'See every movement live',
    desc: 'Real-time AIS vessel and GPS tug tracking across all ports and berths.',
  },
  {
    icon: 'fa-file-invoice-dollar',
    title: 'Billing wired to SAP',
    desc: 'Tariff-rated Statements of Facts flow straight into SAP S/4HANA sales orders.',
  },
  {
    icon: 'fa-shield-alt',
    title: 'Compliant by design',
    desc: 'PPA compulsory-tug checks, MARINA certificate tracking, and an immutable audit ledger.',
  },
];

/**
 * OS-series split authentication screen. Left: TugOS brand panel with the
 * value proposition. Right: the sign-in / register form with a Customer vs
 * Staff audience toggle and (in dev) one-click demo accounts. Wired to
 * useAuth().login / register; on success returns to the intended page.
 *
 * The email/password/submit test ids are preserved for the integration suite.
 */
function AuthForm({ mode }) {
  const isRegister = mode === 'register';
  const { login, register, isAuthenticated, roles } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [audience, setAudience] = useState('staff');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Land on the intended page, but never bounce back to a status/auth page
  // (e.g. a stale 403) or a page this user's roles can't open — fall back to
  // the role-aware dashboard.
  const fromPath = location.state?.from?.pathname;
  if (isAuthenticated) return <Navigate to={safeTarget(fromPath, roles)} replace />;

  const validate = () => {
    const next = {};
    if (!email) next.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) next.email = 'Enter a valid email';
    if (isRegister && !name) next.name = 'Name is required';
    if (!password) next.password = 'Password is required';
    else if (isRegister && password.length < 8)
      next.password = 'Password must be at least 8 characters';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const doLogin = async (creds) => {
    setFormError('');
    setSubmitting(true);
    try {
      const u = await login(creds.email, creds.password);
      navigate(safeTarget(fromPath, u?.roles), { replace: true });
    } catch (err) {
      setFormError(err.message || 'Invalid email or password.');
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!validate()) return;
    if (isRegister) {
      setSubmitting(true);
      try {
        const u = await register(email, name, password);
        navigate(safeTarget(fromPath, u?.roles), { replace: true });
      } catch (err) {
        setFormError(err.message || 'Something went wrong. Please try again.');
        setSubmitting(false);
      }
      return;
    }
    await doLogin({ email, password });
  };

  const signInDemo = (acct) => {
    setEmail(acct.email);
    setPassword(DEMO_PASSWORD);
    doLogin({ email: acct.email, password: DEMO_PASSWORD });
  };

  return (
    <div className="auth-split">
      {/* Brand panel */}
      <aside className="auth-brand">
        <div className="auth-brand__logo mb-5">
          <span className="badge-square">
            <i className="fa fa-anchor" aria-hidden="true" />
          </span>
          TugOS
        </div>
        <h2 className="auth-brand__hero mb-3">Towage that moves at the speed of the tide.</h2>
        <p className="text-white-50 mb-5" style={{ maxWidth: '46ch' }}>
          The integrated towage &amp; pilotage operations platform — booking to dispatch,
          execution to SAP billing, in one harbor-grade system.
        </p>
        <div className="d-flex flex-column gap-4">
          {FEATURES.map((f) => (
            <div className="auth-brand__feature" key={f.title}>
              <span className="icon">
                <i className={`fa ${f.icon}`} aria-hidden="true" />
              </span>
              <div>
                <div className="fw-semibold">{f.title}</div>
                <div className="small text-white-50">{f.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </aside>

      {/* Form panel */}
      <main className="auth-form-panel">
        <div className="auth-form-inner">
          {/* Mobile-only brand */}
          <div className="d-lg-none text-center mb-4">
            <i className="fa fa-anchor fa-2x text-primary" aria-hidden="true" />
            <div className="fw-bold mt-2">TugOS</div>
          </div>

          <h1 className="h3 fw-bold mb-1">{isRegister ? 'Create account' : 'Welcome back'}</h1>
          <p className="text-muted mb-4">
            {isRegister
              ? 'Set up your TugOS staff account.'
              : 'Sign in to the TugOS operations platform.'}
          </p>

          {!isRegister && (
            <div className="auth-segment mb-4" role="tablist" aria-label="Account type">
              <button
                type="button"
                role="tab"
                aria-selected={audience === 'customer'}
                className={audience === 'customer' ? 'active' : ''}
                onClick={() => setAudience('customer')}
                data-testid="audience-customer"
              >
                Customer
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={audience === 'staff'}
                className={audience === 'staff' ? 'active' : ''}
                onClick={() => setAudience('staff')}
                data-testid="audience-staff"
              >
                Staff / Admin
              </button>
            </div>
          )}

          {formError && (
            <div className="alert alert-danger py-2" role="alert" data-testid="auth-error">
              {formError}
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            {isRegister && (
              <div className="mb-3">
                <label htmlFor="name" className="form-label small fw-semibold">Full name</label>
                <input
                  id="name"
                  type="text"
                  className={`form-control${errors.name ? ' is-invalid' : ''}`}
                  value={name}
                  autoComplete="name"
                  onChange={(e) => setName(e.target.value)}
                />
                {errors.name && <div className="invalid-feedback">{errors.name}</div>}
              </div>
            )}

            <div className="mb-3">
              <label htmlFor="email" className="form-label small fw-semibold">
                {audience === 'customer' && !isRegister ? 'Account email' : 'Work email'}
              </label>
              <input
                id="email"
                type="email"
                className={`form-control${errors.email ? ' is-invalid' : ''}`}
                value={email}
                autoComplete="email"
                placeholder="you@company.com"
                onChange={(e) => setEmail(e.target.value)}
                data-testid="email"
              />
              {errors.email && <div className="invalid-feedback">{errors.email}</div>}
            </div>

            <div className="mb-3">
              <div className="d-flex justify-content-between">
                <label htmlFor="password" className="form-label small fw-semibold">Password</label>
                {!isRegister && (
                  <span className="small text-muted">Demo password: {DEMO_PASSWORD}</span>
                )}
              </div>
              <div className="input-group">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  className={`form-control${errors.password ? ' is-invalid' : ''}`}
                  value={password}
                  autoComplete={isRegister ? 'new-password' : 'current-password'}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="password"
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <i className={`fa ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`} aria-hidden="true" />
                </button>
                {errors.password && <div className="invalid-feedback">{errors.password}</div>}
              </div>
            </div>

            <button
              type="submit"
              className="btn btn-primary w-100 py-2 fw-semibold"
              disabled={submitting}
              data-testid="submit"
            >
              {submitting && (
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
              )}
              {isRegister ? 'Create account' : 'Sign In'}
            </button>
          </form>

          <p className="text-center text-muted small mt-4 mb-0">
            {isRegister ? (
              <>Already have an account? <Link to="/login">Sign in</Link></>
            ) : audience === 'customer' ? (
              <>No account?{' '}
                <Link to="/customer-registration-account-portal">Register a customer account</Link>
              </>
            ) : (
              <>No account? <Link to="/register">Register here</Link></>
            )}
          </p>

          {showDemo && !isRegister && (
            <div className="mt-4">
              <div
                className="text-center text-muted text-uppercase small mb-2"
                style={{ letterSpacing: '0.08em' }}
              >
                <i className="fa fa-flask me-1" aria-hidden="true" /> Demo accounts — click to sign in
              </div>
              <div className="d-flex flex-column gap-2">
                {DEMO_ACCOUNTS[audience].map((acct) => (
                  <button
                    key={acct.email}
                    type="button"
                    className="demo-account"
                    disabled={submitting}
                    onClick={() => signInDemo(acct)}
                    data-testid={`demo-${acct.email}`}
                  >
                    <span className="fw-semibold text-dark">{acct.name}</span>
                    <span className="text-muted"> — {acct.role}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export function LoginPage() {
  return <AuthForm mode="login" />;
}

export function RegisterPage() {
  return <AuthForm mode="register" />;
}

export default LoginPage;
